import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
/* eslint-disable  import/no-cycle */
import { RootState } from 'store/rootReducer';
import { ApiErrors, Loaders } from 'api/types';
import keyBy from 'lodash/keyBy';
import set from 'lodash/set';
import { UNAUTHORIZED, FORBIDDEN, NOT_FOUND } from 'http-status-codes';
import { i18n } from 'helpers/i18n';
import { ReportTemplateField } from 'features/ReportTemplates/reportTemplates';
import { Document } from 'features/Documents/documents';
import { axiosInstance } from '../axiosInstance';

export type Report = {
  author_full_name: string;
  author_id: string;
  can_edit: boolean;
  created_at: string;
  description: string;
  documents: Document[];
  editor_full_name: string;
  editor_id: string;
  fields: ReportTemplateField[];
  id: string;
  link_type: string;
  name: string;
  one_shot_request_name: string | null;
  public: boolean;
  qr: string;
  template_id: string;
  template_version: number;
  updated_at: string;
  version: number;
  versions: number | null;
};

type ReportsState = {
  apiErrors: ApiErrors;
  currentReport?: Report | undefined;
  list: {
    [key: string]: Report;
  };
  loaders: Loaders;
};

const instancesApiPrefix = '/datasheets/instances';

// TODO: use object instead of  array
const initialState: ReportsState = {
  apiErrors: {
    loadingError: '',
    savingError: ''
  },
  list: {},
  loaders: {
    isSaving: false,
    isDeletingItems: {}
  }
};

export const fetchReports = createAsyncThunk<Report[], null, { rejectValue: string }>(
  'reports/fetchReports',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(instancesApiPrefix);
      const {
        data: { data_sheets }
      } = response;
      return data_sheets;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('errors.unauthorized'));
      }
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const fetchReport = createAsyncThunk<any, string, { rejectValue: string }>(
  'reportTemplates/fetchTemplate',
  async (reportId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${instancesApiPrefix}/${reportId}`);
      const { data } = response;
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('errors.unauthorized'));
      }
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const deleteReport = createAsyncThunk<string, string, { rejectValue: string }>(
  'reports/deleteReport',
  async (reportId, { dispatch, rejectWithValue }) => {
    try {
      dispatch(updateListDelete({ id: reportId, value: true }));
      await axiosInstance.delete(`${instancesApiPrefix}/${reportId}`);
      return reportId;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case FORBIDDEN:
        case UNAUTHORIZED:
          return rejectWithValue(i18n.t('reports.you cannot delete this report'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('reports.not found'));
        default:
          return rejectWithValue(i18n.t('errors.something went wrong'));
      }
    } finally {
      dispatch(updateListDelete({ id: reportId, value: false }));
    }
  }
);

interface SaveReportParams {
  id?: string;
  report: {
    code: string;
    country_code: string;
    name: string;
  };
}

export const saveReport = createAsyncThunk<Report, SaveReportParams, { rejectValue: string }>(
  'reports/saveReport',
  async ({ report, id }, { rejectWithValue }) => {
    let response;
    try {
      response = await axiosInstance.put(`${instancesApiPrefix}/${id}`, report);
      const { data } = response;
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('reports.you cannot update or create report'));
      }

      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

const reportsSlice = createSlice({
  initialState,
  name: 'reports',
  reducers: {
    updateListDelete: (state, { payload }: PayloadAction<{ id: string; value: boolean }>) => {
      const { id, value } = payload;
      if (value) {
        set(state, `loaders.deletingErrorItems.${id}`, '');
      }
      set(state, `loaders.isDeletingItems.${id}`, value);
    },
    resetReports: state => {
      state = initialState;
    },
    resetCurrentReport: state => {
      state.currentReport = undefined;
    },
    closeError: state => {
      state.apiErrors = {};
    }
  },
  extraReducers: builder =>
    builder
      .addCase(fetchReports.fulfilled, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.list = keyBy(payload, 'id');
      })
      .addCase(fetchReports.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchReports.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(fetchReport.fulfilled, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.currentReport = payload;
      })
      .addCase(fetchReport.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchReport.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(deleteReport.fulfilled, (state, { payload }) => {
        delete state.list[`${payload}`];
      })
      .addCase(saveReport.fulfilled, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.list[payload.id] = payload;
      })
      .addCase(saveReport.pending, state => {
        state.loaders.isSaving = true;
        state.apiErrors.savingError = '';
      })
      .addCase(saveReport.rejected, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
});

const { actions, reducer } = reportsSlice;
export { reducer as reports };
export const { closeError, updateListDelete, resetReports, resetCurrentReport } = actions;

export const getReport = (state: RootState) => state.reports.currentReport;
export const getReports = (state: RootState) => state.reports.list;
export const getReportsLoaders = (state: RootState) => state.reports.loaders;
export const getReportsApiErrors = (state: RootState) => state.reports.apiErrors;
